import { useEffect, useState, useRef, useCallback } from "react";
import { useDateFilter } from "../../../../contexts/dashboard/DateFilterContext";
import accessURL from "../../../../components/utils/superAccess";

interface DateFilter {
  startDate: string;
  endDate: string;
  selectedYear: number;
}

interface Company {
  id: number;
  customID: string;
  name: string;
  call_rate: number;
  total_reports: number;
  total_targets: number;
}

interface TopCompaniesResponse {
  status: string;
  code: number;
  data: {
    companies: Company[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

const fetchTopCompanies = async ({
  startDate,
  endDate,
  selectedYear,
  page,
  limit,
}: {
  startDate: string;
  endDate: string;
  selectedYear: number;
  page: number;
  limit: number;
}) => {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  if (selectedYear) params.append("year", selectedYear.toString());
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  return await accessURL<TopCompaniesResponse>(
    `super-admin/topCompanies?${params.toString()}`
  );
};

const TopCompanies = () => {
  const [page, setPage] = useState(1);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const PER_PAGE = 10;
  const dateFilter = useDateFilter() as DateFilter;
  const {
    startDate = "",
    endDate = "",
    selectedYear = new Date().getFullYear(),
  } = dateFilter;

  const lastCompanyRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;

      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  useEffect(() => {
    setPage(1);
    setCompanies([]);
    setHasMore(true);
  }, [dateFilter]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchTopCompanies({
          startDate,
          endDate,
          selectedYear,
          page,
          limit: PER_PAGE,
        });

        const newCompanies = response?.data.data.companies;
        setCompanies((prev) =>
          page === 1 ? newCompanies : [...prev, ...newCompanies]
        );
        setHasMore(response?.data.data.page < response?.data.data.total_pages);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Error fetching top companies";
        setError(errorMessage);
        console.error("Error fetching top companies:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, selectedYear, page]);

  if (error) {
    return (
      <div className="w-full rounded-lg bg-red-50 p-4">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg bg-white shadow-sm">
      <div className="p-4">
        <div
          ref={scrollContainerRef}
          className="h-[600px] overflow-y-auto space-y-4 pr-2 scroll-smooth"
        >
          {companies.map((company, index) => (
            <div
              key={company.id}
              ref={index === companies.length - 1 ? lastCompanyRef : null}
              className="rounded-lg bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md border border-gray-200"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {company.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    ID: {company.customID}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-600">
                    Call Rate: {company.call_rate.toFixed(2)}%
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-600">Total Reports</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {company.total_reports}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-600">
                    Total Customer Targets
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {company.total_targets}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-center p-4">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopCompanies;
